import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { ChevronRight } from "lucide-react";
import { Badge, type BadgeTone } from "../atoms/Badge/Badge.js";
import { Button } from "../atoms/Button/index.js";
import type { PlayerAIToolProposalPayload } from "./playerai.types";
import { proposalCollapsedSummary } from "./proposal-collapse";
import { proposalDisplayStatus } from "./proposal-status";

const STATUS_TONES: Record<PlayerAIToolProposalPayload["status"], BadgeTone> = {
  pending: "watch",
  applying: "info",
  rejected: "neutral",
  expired: "neutral",
  applied: "healthy",
  failed: "critical",
  stale: "watch",
};

/**
 * Task 13 — an older, completed proposal section folds into one compact row
 * ("▸ 2 canvas changes · Applied") that expands in place to its original card.
 *
 * Presentation only: the card is the same component with the same actions,
 * and nothing here touches order, status, approval or application.
 *
 * Three things keep it from collapsing under a reader:
 * - A section that was on screen EXPANDED stays expanded while it remains on
 *   screen, even after a newer proposal makes it fold-eligible; it folds only
 *   once it has left the thread's viewport (so the fold happens out of sight,
 *   and the thread's anchoring keeps the reader's position).
 * - A section the reader expanded stays expanded for the rest of the session.
 * - Collapsing it again is always the reader's own choice.
 */
export function CollapsibleProposal({
  payload,
  collapsible,
  scrollRootRef,
  children,
}: {
  payload: PlayerAIToolProposalPayload;
  /** Older than the latest proposal and not needing attention. */
  collapsible: boolean;
  scrollRootRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) {
  const [userExpanded, setUserExpanded] = useState(false);
  const [heldForReading, setHeldForReading] = useState(false);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const expanded = !collapsible || userExpanded || heldForReading;
  const expandedRef = useRef(expanded);
  useLayoutEffect(() => {
    expandedRef.current = expanded;
  });
  const panelId = useId();

  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries.at(-1);
        if (!entry) return;
        if (!entry.isIntersecting) setHeldForReading(false);
        else if (expandedRef.current) setHeldForReading(true);
      },
      { root: scrollRootRef.current },
    );
    observer.observe(row);
    return () => observer.disconnect();
  }, [scrollRootRef]);

  const status = proposalDisplayStatus(payload);
  const summary = proposalCollapsedSummary(payload);
  // ONE structure in both states, so a section becoming fold-eligible keeps
  // its card mounted (and whatever the reader had open inside it).
  return (
    <div ref={rowRef} className="min-w-0" data-proposal-section>
      {collapsible ? (
        <Button
          variant="ghost"
          size="sm"
          className="w-full max-w-md justify-start"
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={`${summary} · ${status.label}`}
          onClick={() => {
            setUserExpanded(!expanded);
            setHeldForReading(false);
          }}
        >
          <ChevronRight
            className={`h-3.5 w-3.5 shrink-0 transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
            aria-hidden
          />
          <span className="min-w-0 truncate text-start font-normal text-pb-text-secondary">
            {summary}
          </span>
          <span aria-hidden className="text-pb-text-muted">
            ·
          </span>
          <Badge
            tone={STATUS_TONES[payload.status]}
            size="xs"
            shape="pill"
            className="shrink-0"
          >
            {status.label}
          </Badge>
        </Button>
      ) : null}
      <div id={panelId} hidden={!expanded}>
        {expanded ? children : null}
      </div>
    </div>
  );
}
